import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel from "@/pages/movies/components/FilterPanel";
import { movies } from "@/mocks/movies";

const PAGE_SIZE = 8;

export default function MoviesPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("release");
  const [page, setPage] = useState(1);

  const genres = useMemo(
    () => Array.from(new Set(movies.flatMap((m) => m.genres))),
    [],
  );
  const regions = useMemo(
    () => Array.from(new Set(movies.map((m) => m.region))),
    [],
  );
  const years = useMemo(
    () => Array.from(new Set(movies.map((m) => m.year))).sort((a, b) => b - a),
    [],
  );

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (keyword && !m.title.toLowerCase().includes(keyword.toLowerCase())) return false;
      if (genre !== "全部" && !m.genres.includes(genre)) return false;
      if (region !== "全部" && m.region !== region) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      if (status !== "all" && m.status !== status) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else sorted.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    return sorted;
  }, [keyword, genre, region, year, status, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setStatus("all");
    setSort("release");
    setPage(1);
  };

  const update = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />

        <div className="mt-5 mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            电影分类
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            按类型、地区、年份与状态筛选影片，支持按上映时间、评分与热度排序。
          </p>
          {keyword && (
            <p className="mt-2 text-sm text-primary-600">
              当前搜索关键词：「{keyword}」，共找到 {filtered.length} 部影片
            </p>
          )}
        </div>

        <FilterPanel
          genres={genres}
          regions={regions}
          years={years}
          genre={genre}
          region={region}
          year={year}
          status={status}
          sort={sort}
          onGenre={update(setGenre)}
          onRegion={update(setRegion)}
          onYear={update(setYear)}
          onStatus={update(setStatus)}
          onSort={update(setSort)}
        />

        <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
          <span>共 {filtered.length} 部影片</span>
          <span>第 {safePage} / {totalPages} 页</span>
        </div>

        {pageItems.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-film-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              没有找到符合条件的影片
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              试试调整筛选条件，或重置后重新浏览全部影片。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        ) : (
          <div
            data-product-shop
            className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
          >
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        <Pagination current={safePage} total={totalPages} onChange={setPage} />
      </div>
    </SiteLayout>
  );
}